'use client';

import TruncatedText from '~/components/TruncatedText';
import { cn } from '~/lib/utils';

export type FeaturePageHeaderProps = {
  title: React.ReactNode;
  actions?: React.ReactNode;
  /** Inline with the title (same row), not a second header row. */
  tabs?: React.ReactNode;
  /**
   * Selected filters / secondary controls.
   * Render outside `<header>` (below its bottom divider). Keep sticky by returning a
   * full-width row from the slot — FeaturePageHeader’s outer wrapper is sticky.
   * Pass `null` / omit when idle so no empty row appears.
   */
  filters?: React.ReactNode;
  className?: string;
  /** Truncate long dynamic titles (e.g. project names). */
  truncateTitle?: boolean;
};

/** Opaque surface matching FeaturePageShell’s muted canvas (avoids see-through sticky). */
export const featurePageStickySurfaceClassName =
  'bg-[color-mix(in_oklab,var(--muted)_40%,var(--background))]';

/** Shared chrome for the sticky filters row under FeaturePageHeader. */
export const featurePageFiltersRowClassName = cn(
  'border-border/60 border-b',
  featurePageStickySurfaceClassName,
);

export const featurePageFiltersInnerClassName =
  'mx-auto flex w-full max-w-6xl flex-wrap items-center gap-1.5 px-4 py-2 md:px-6 xl:max-w-none';

/**
 * Compact sticky feature-area header matching Home / Overview chrome.
 * Title + tabs + actions on the h-12 row; optional selected filters below the divider.
 */
export default function FeaturePageHeader({
  title,
  actions,
  tabs,
  filters,
  className,
  truncateTitle = false,
}: FeaturePageHeaderProps) {
  return (
    <div
      data-feature-page-header=""
      className={cn('sticky top-0 z-20 shrink-0', featurePageStickySurfaceClassName, className)}
    >
      <header className="border-border/60 border-b">
        <div className="mx-auto flex h-12 w-full max-w-6xl items-stretch gap-3 px-4 md:px-6 xl:max-w-none">
          <div className="flex min-w-0 flex-1 items-stretch gap-5">
            <h1
              className={cn(
                'text-foreground flex items-center text-base font-semibold tracking-tight',
                truncateTitle && 'min-w-0',
              )}
            >
              {truncateTitle && (typeof title === 'string' || typeof title === 'number') ? (
                <TruncatedText className="min-w-0">{String(title)}</TruncatedText>
              ) : (
                title
              )}
            </h1>
            {tabs ? <div className="flex min-w-0 shrink-0 items-stretch">{tabs}</div> : null}
          </div>
          {actions ? (
            <div className="flex shrink-0 items-center gap-1.5">{actions}</div>
          ) : null}
        </div>
      </header>
      {filters}
    </div>
  );
}

FeaturePageHeader.chrome = true as const;
